import { useState, useMemo } from "react";
import { Draggable } from "@hello-pangea/dnd";
import { alpha, useTheme } from "@mui/material/styles";
import DSTextField from "views/system-design/DSTextField";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSChip from "views/system-design/DSChip";
import DSIconButton from "views/system-design/DSIconButton";
import DSTooltip from "views/system-design/DSTooltip";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PersonIcon from "@mui/icons-material/Person";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Ticket,
  TYPE_COLORS,
  LABEL_COLORS,
  PRIORITY_COLORS,
} from "../../types";
import {
  formatDate,
  getNextStatus,
  getNextStatusLabel,
  getTypeLabel,
  getPriorityLabel,
  isImage,
  canChangeStatus,
  resolveUserName,
  resolveUserId,
} from "../../utils";
import { Author } from "api/author";
import TimeInStatus from "../shared/TimeInStatus";

interface TicketCardProps {
  ticket: Ticket;
  index: number;
  onClick: () => void;
  onQuickUpdate: (ticketId: string, updates: Partial<Ticket>) => void;
  currentUser: string;
  users: Author[];
}

function SubtaskProgressBar({ ticket }: { ticket: Ticket }) {
  const completed = ticket.subtasks.filter(
    (subtask) => subtask.completed,
  ).length;
  const total = ticket.subtasks.length;
  const percent = (completed / total) * 100;

  return (
    <DSBox
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mb: 1,
      }}
    >
      <CheckCircleOutlineIcon
        sx={{
          fontSize: 14,
          color: completed === total ? "success.main" : "text.disabled",
        }}
      />
      <DSTypography
        variant="caption"
        sx={{
          color: completed === total ? "success.main" : "text.secondary",
          fontWeight: 600,
          fontSize: 11,
        }}
      >
        {completed}/{total}
      </DSTypography>
      <DSBox
        sx={{
          flex: 1,
          height: 4,
          bgcolor: "white.12",
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <DSBox
          sx={{
            width: `${percent}%`,
            height: "100%",
            bgcolor: completed === total ? "success.main" : "info.main",
            borderRadius: 2,
            transition: "width 0.3s",
          }}
        />
      </DSBox>
    </DSBox>
  );
}

export default function TicketCard({
  ticket,
  index,
  onClick,
  onQuickUpdate,
  currentUser,
  users,
}: TicketCardProps) {
  const theme = useTheme();
  const nextStatusLabel = getNextStatusLabel(ticket.status);
  const [titleEditing, setTitleEditing] = useState<{ draft: string } | null>(
    null,
  );

  const imageAttachments = useMemo(
    () => ticket.attachments?.filter((attachment) => isImage(attachment.type)),
    [ticket.attachments],
  );

  const handleMoveNext = (event: React.MouseEvent) => {
    event.stopPropagation();
    const next = getNextStatus(ticket.status);
    if (!next) return;
    if (!canChangeStatus(ticket, next)) return;
    onQuickUpdate(ticket.id, { status: next });
  };

  const handleToggleBacklog = (event: React.MouseEvent) => {
    event.stopPropagation();
    onQuickUpdate(ticket.id, { isBacklog: !ticket.isBacklog });
  };

  const handleAssignToMe = (event: React.MouseEvent) => {
    event.stopPropagation();
    onQuickUpdate(ticket.id, { assignee: currentUser });
  };

  const resolvedAssigneeId = resolveUserId(ticket.assignee, users);

  return (
    <Draggable draggableId={ticket.id} index={index}>
      {(provided, snapshot) => (
        <DSBox
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => {
            if (titleEditing) return;
            onClick();
          }}
          sx={{
            position: "relative",
            bgcolor: ticket.isBacklog
              ? alpha(theme.palette.warning.light, 0.15)
              : "surface.card",
            border: 1,
            borderColor: "divider",
            borderRadius: 1.5,
            p: 1.5,
            mb: 1,
            boxShadow: snapshot.isDragging ? 4 : 1,
            cursor: "pointer",
            transition: "box-shadow 0.2s",
            "&:hover": { boxShadow: 3 },
            "&:hover .quick-actions": { opacity: 1 },
          }}
        >
          <DSBox
            className="quick-actions"
            sx={{
              position: "absolute",
              top: 4,
              right: 4,
              display: "flex",
              gap: 0.25,
              opacity: 0,
              transition: "opacity 0.15s",
              bgcolor: "paper.elevation6",
              borderRadius: 1,
              boxShadow: 1,
              p: 0.25,
              zIndex: 1,
            }}
          >
            {nextStatusLabel && (
              <DSTooltip title={`Move to ${nextStatusLabel}`}>
                <span>
                  <DSIconButton size="small" onClick={handleMoveNext}>
                    <ArrowForwardIcon sx={{ fontSize: 16 }} />
                  </DSIconButton>
                </span>
              </DSTooltip>
            )}
            <DSTooltip
              title={ticket.isBacklog ? "Move to board" : "Move to backlog"}
            >
              <span>
                <DSIconButton size="small" onClick={handleToggleBacklog}>
                  {ticket.isBacklog ? (
                    <UnarchiveIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <ArchiveIcon sx={{ fontSize: 16 }} />
                  )}
                </DSIconButton>
              </span>
            </DSTooltip>
            {resolvedAssigneeId !== currentUser && (
              <DSTooltip title="Assign to me">
                <span>
                  <DSIconButton size="small" onClick={handleAssignToMe}>
                    <PersonAddIcon sx={{ fontSize: 16 }} />
                  </DSIconButton>
                </span>
              </DSTooltip>
            )}
          </DSBox>

          {imageAttachments.length > 0 && (
            <DSBox
              sx={{
                display: "flex",
                gap: 0.5,
                mb: 1,
                mx: -1.5,
                mt: -1.5,
                overflow: "hidden",
                borderTopLeftRadius: 6,
                borderTopRightRadius: 6,
              }}
            >
              {imageAttachments.map((attachment) => (
                <img
                  key={attachment.id}
                  src={attachment.url}
                  alt={attachment.name}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 80,
                    objectFit: "cover",
                  }}
                />
              ))}
            </DSBox>
          )}

          <DSBox
            sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
          >
            <DSTypography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              {ticket.key}
            </DSTypography>
            <DSChip
              label={getTypeLabel(ticket.type)}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                bgcolor: TYPE_COLORS[ticket.type] || "grey.600",
                color: "common.white",
              }}
            />
            {ticket.priority && (
              <DSChip
                label={getPriorityLabel(ticket.priority)}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  bgcolor: PRIORITY_COLORS[ticket.priority] || "grey.500",
                  color: "common.white",
                }}
              />
            )}
          </DSBox>

          {titleEditing ? (
            <DSTextField
              value={titleEditing.draft}
              onChange={(event) =>
                setTitleEditing((prev) =>
                  prev ? { draft: event.target.value } : prev,
                )
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  if (
                    titleEditing.draft.trim() &&
                    titleEditing.draft !== ticket.title
                  ) {
                    onQuickUpdate(ticket.id, {
                      title: titleEditing.draft.trim(),
                    });
                  }
                  setTitleEditing(null);
                }
                if (event.key === "Escape") {
                  setTitleEditing(null);
                }
              }}
              onBlur={() => {
                if (
                  titleEditing.draft.trim() &&
                  titleEditing.draft !== ticket.title
                ) {
                  onQuickUpdate(ticket.id, {
                    title: titleEditing.draft.trim(),
                  });
                }
                setTitleEditing(null);
              }}
              onClick={(event) => event.stopPropagation()}
              autoFocus
              size="small"
              multiline
              fullWidth
              sx={{ mb: 1, "& .MuiInputBase-input": { fontSize: 14, p: 0.5 } }}
            />
          ) : (
            <DSTypography
              variant="body2"
              sx={{ fontWeight: 500, mb: 1 }}
              onDoubleClick={(event) => {
                event.stopPropagation();
                setTitleEditing({ draft: ticket.title });
              }}
            >
              {ticket.title}
            </DSTypography>
          )}

          {ticket.labels.length > 0 && (
            <DSBox sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
              {ticket.labels.map((label) => (
                <DSChip
                  key={label}
                  label={label.toUpperCase()}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 10,
                    fontWeight: 700,
                    bgcolor: LABEL_COLORS[label] ?? "grey.500",
                    color: "common.white",
                  }}
                />
              ))}
            </DSBox>
          )}

          {ticket.subtasks.length > 0 && <SubtaskProgressBar ticket={ticket} />}

          {ticket.targetVersion && (
            <DSBox sx={{ mb: 1 }}>
              <DSChip
                label={`Target version - ${ticket.targetVersion}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  bgcolor: "info.main",
                  color: "common.white",
                }}
              />
            </DSBox>
          )}

          <DSBox
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <DSBox sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              {ticket.creatorId && (
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                  <DSTypography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Reporter: {resolveUserName(ticket.creatorId, users)}
                  </DSTypography>
                </DSBox>
              )}
              {ticket.assignee && (
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <PersonIcon sx={{ fontSize: 14, color: "info.main" }} />
                  <DSTypography variant="caption" sx={{ color: "info.main" }}>
                    Assignee: {resolveUserName(ticket.assignee, users)}
                  </DSTypography>
                </DSBox>
              )}
            </DSBox>
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 0.25,
              }}
            >
              <TimeInStatus ticket={ticket} />
              {ticket.dueDate && (
                <DSBox sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarTodayIcon
                    sx={{ fontSize: 12, color: "text.secondary" }}
                  />
                  <DSTypography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {formatDate(ticket.dueDate)}
                  </DSTypography>
                </DSBox>
              )}
            </DSBox>
          </DSBox>
        </DSBox>
      )}
    </Draggable>
  );
}
